import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Container, Paper, Title, Divider, Grid, Button, Group, Text, Stack, Input, Checkbox, TextInput } from '@mantine/core';
import { PaymentInputsWrapper, usePaymentInputs } from "react-payment-inputs";
import images from "react-payment-inputs/images";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { FiUser, FiMail, FiPhone, FiCreditCard, FiDollarSign, FiCalendar, FiClock, FiHome } from 'react-icons/fi';

import { RHFTextInput, RHFSelect, RHFCheckbox, RHFTextarea, RHFGoogleAddress, RHFCustomDatePicker, RHFNumberInput } from "./FormComponents";
import './enrollment.css'
import base_url from "../../api/bootapi";
import Nav from "../NavBar/Nav";
import FooterNew from "../NavBar/FooterNew";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";



// Reusable inline style for the country/region selects to match Mantine's inputs
const selectStyle = {
    width: '100%',
    height: '40px',
    padding: '0 12px',
    borderRadius: '4px',
    border: '1px solid #ced4da',
    fontSize: '14px',
    backgroundColor: '#fff',
    fontFamily: 'inherit'
};

const ticketOptions = [
    { label: "First", value: "First" },
    { label: "Business", value: "Business" },
    { label: "Premium", value: "Premium" },
    { label: "Economy Class (Cost will be advised)", value: "Economy Class" }
];

const TourTripEnrollment = () => {
    const { tour_url_name, sub_Tour_url_name } = useParams();

    const navigate = useNavigate();
    const [backdropopen, setbackdropopen] = useState(false);

    const [tourId, setTourId] = useState(null);
    const [subTourId, setSubTourId] = useState(null);


    // --- API & Core State ---
    const [loading, setLoading] = useState(false);
    const [tourDetails, setTourDetails] = useState(null);
    const [mainTourDetails, setMainTourDetails] = useState(null);

    // --- Custom Component States ---
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");

    const [cardDetails, setCardDetails] = useState({
        card_number: null,
        card_expiry_year: null,
        card_cvv: null,
    });

    const { wrapperProps, getCardImageProps, getCardNumberProps, getExpiryDateProps, getCVCProps } = usePaymentInputs();

    // --- Form Initialization ---
    const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
        defaultValues: {
            first_name: "", last_name: "", middle_name: "", gender: "", date_of_birth: null,
            email_address: "", mobile_number: "", passport_number: "", passport_expiration_date: null,
            city: "", state: "", zip: "", country: "United States",
            options: { fullPackage: false, accommodation: false, singleRoomCost: false, insurance: false, otherCost: false },
            special_ticket_preference: "",
            payment_method: "Cash", card_holder_name: "", sameAsAbove: false,
            depositRequired: false, enableExtraDeposit: false, extraDepositAmount: "",
            billing: { city: "", state: "", zip: "", country: "United States" },
            remarks: "",
        }
    });

    const options = watch("options");
    const paymentMethod = watch("payment_method");
    const sameAsAbove = watch("sameAsAbove");

    const selectedCountry = watch("country");
    const selectedBillingCountry = watch("billing.country");

    const depositRequired = watch("depositRequired");
    const enableExtraDeposit = watch("enableExtraDeposit");



    // Enforce mutual exclusivity for options
    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === 'options.fullPackage' && value.options?.fullPackage) {
                setValue('options.accommodation', false);
            }
            if (name === 'options.accommodation' && value.options?.accommodation) {
                setValue('options.fullPackage', false);
            }
            // Clean up extra deposit if unchecked
            if (name === 'enableExtraDeposit' && !value.enableExtraDeposit) {
                setValue('extraDepositAmount', "");
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    // Derived Pricing Calculations
    const calculateTotal = () => {
        if (!tourDetails) return 0;
        let total = 0;
        if (options.fullPackage) total += Number(tourDetails.full_package_price || 0);
        if (options.accommodation) total += Number(tourDetails.accommodation_only_price || 0);
        if (options.singleRoomCost) total += Number(tourDetails.single_room_cost || 0);
        if (options.insurance) total += Number(tourDetails.insurance_price || 0);
        if (options.otherCost) total += Number(tourDetails.other_cost || 0);
        return total;
    };
    const totalPrice = calculateTotal();



    const minimumDeposit = Number(tourDetails?.minimum_deposit || 0);
    const minimumDepositRequired = tourDetails?.minimum_deposit_mandatory || false;



    const extraDepositAmount = watch("extraDepositAmount");

    // Calculate Payment Due Now
    const calculatePaymentDue = () => {
        let due = 0;
        if (paymentMethod === "Card") {
            if (depositRequired) due += Number(minimumDeposit || 0);
            if (enableExtraDeposit) due += Number(extraDepositAmount || 0);
        }
        return due;
    };

    const paymentDue = calculatePaymentDue();

    useEffect(() => {
        setValue("advance_payment", paymentDue);
    }, [paymentDue, setValue]);


    useEffect(() => {
        setValue('advance_payment', 0);
        setValue('extraDepositAmount', 0);
        setValue('depositRequired', false);
        setValue('enableExtraDeposit', false);

        if (paymentMethod === 'Zelle') {
            setValue('advance_payment', minimumDeposit);

        }

    }, [paymentMethod, setValue])



    // --- Address Logic ---
    const parseAddressComponents = (components = []) => {
        const result = { city: "", state: "", postalCode: "", country: "" };
        components.forEach((c) => {
            if (c.types.includes("locality") || (!result.city && c.types.includes("sublocality_level_1")) || (!result.city && c.types.includes("administrative_area_level_2"))) {
                result.city = c.long_name;
            }
            if (c.types.includes("administrative_area_level_1")) result.state = c.long_name;
            if (c.types.includes("postal_code")) result.postalCode = c.long_name;
            if (c.types.includes("country")) result.country = c.long_name;
        });
        return result;
    };

    const onPlaceSelect_JS = (val, main = true) => {
        if (!val) {
            if (main) { setValue("city", ""); setValue("zip", ""); setValue("state", ""); setValue("country", ""); }
            else { setValue("billing.city", ""); setValue("billing.zip", ""); setValue("billing.state", ""); setValue("billing.country", ""); }
            return;
        }
        let placeId = typeof val === 'object' ? val.value : val;
        if (!window.google || !placeId) return;

        const ps = new window.google.maps.places.PlacesService(document.createElement("div"));
        ps.getDetails({ placeId, fields: ["address_components"] }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                const parsed = parseAddressComponents(place.address_components || []);
                if (main) {
                    setValue("city", parsed.city || ""); setValue("zip", parsed.postalCode || "");
                    setValue("state", parsed.state || ""); setValue("country", parsed.country || "United States");
                } else {
                    setValue("billing.city", parsed.city || ""); setValue("billing.zip", parsed.postalCode || "");
                    setValue("billing.state", parsed.state || ""); setValue("billing.country", parsed.country || "United States");
                }
            }
        });
    };

    useEffect(() => { onPlaceSelect_JS(address1, true); }, [address1]);
    useEffect(() => { onPlaceSelect_JS(address2, false); }, [address2]);

    // --- Data Fetching ---

    // useEffect(() => {
    //     setbackdropopen(true);
    //     // Fetch both simultaneously
    //     Promise.all([
    //         axios.get(`${base_url.api1}/tourapp/get_trip?id=${params.tour_Enrollment_id}`),
    //         axios.get(`${base_url.api1}/tourapp/subtour/by_tour/${params.tour_Enrollment_id}`)
    //     ])
    //         .then(([mainRes, subRes]) => {
    //             const main = mainRes.data.Tour[0];
    //             const subs = subRes.data.SubTour || [];

    //             setMainTourDetails(main);
    //             setTourDetails(main);
    //             setValue("payment_method", main?.payment_methods?.[0] || "Cash");
    //             if (main.minimum_deposit_mandatory) {
    //                 setValue("depositRequired", true);
    //             }

    //             // Combine them: Main tour is first, followed by sub-tours
    //             const allOptions = [main, ...subs];
    //             setSelectableTours(allOptions);

    //             // Default selection: Main Tour (index 0)
    //             handleSubTourSelect(main);
    //         })
    //         .finally(() => setbackdropopen(false));
    // }, [params.tour_Enrollment_id, setValue]);
    useEffect(() => {
        setbackdropopen(true);

        if (tour_url_name) {

            getMainTourDetails();
        }

        if (sub_Tour_url_name) {
            getSubTourDetails();
        }

    }, [tour_url_name, sub_Tour_url_name]);




    const getMainTourDetails = async () => {
        axios.get(`${base_url.api1}/tourapp/get_trip?url_name=${tour_url_name}`)
            .then((res) => {
                const data = res.data.Tour[0];
                setTourId(data?.id)
                if (!sub_Tour_url_name) {

                    setTourDetails(data);
                    setMainTourDetails(data);
                    if (data.minimum_deposit_mandatory) {
                        setValue("depositRequired", true);
                    }
                }
                setValue("payment_method", data?.payment_methods?.[0] || "Cash");

            }).catch((e) => {
                console.log(e.message)
            })
            .finally(() => setbackdropopen(false));
    }

    const getSubTourDetails = async () => {
        axios.get(`${base_url.api1}/tourapp/subtour?url_name=${sub_Tour_url_name}`)
            .then((res) => {
                const data = res.data.SubTour[0];
                setSubTourId(data?.id)

                setTourDetails(data);

                if (data.minimum_deposit_mandatory) {
                    setValue("depositRequired", true);
                }
            }).catch((e) => {
                console.log(e.message)
            })
            .finally(() => setbackdropopen(false));
    }


    // Name formatting function
    const formatName = (name) => {
        if (typeof name !== 'string' || !name.trim()) return "";
        return name.trim().charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    // Address extraction
    const getAddressValue = (addr) =>
        typeof addr === 'object' ? addr?.label ?? '' : addr ?? '';

    // Date formatting for API
    const formatApiDate = (dateStr) => {
        if (!dateStr) return null;
        return moment(dateStr, "MM/DD/YYYY").format("YYYY-MM-DDThh:mm:ss");
    };

    const formatToDate = (dateStr) => {
        if (!dateStr) return null;
        return moment(dateStr, "MM/DD/YYYY").format("YYYY-MM-DD");
    };

    // --- Submit ---
    const onSubmit = async (data) => {
        // 1. Package Validation
        if (!options.fullPackage && !options.accommodation) {
            Swal.fire({ icon: "error", title: "Please select Full Package or Accommodation" });
            return;
        }

        // 2. Deposit Validation
        if (paymentMethod === "Card" && minimumDepositRequired && !depositRequired) {
            Swal.fire({ icon: "error", title: "Minimum Deposit is Required" });
            return;
        }

        // 3. Card Details Validation
        let card_expiry_month = null;
        let card_expiry_year = null;

        if (data.payment_method === "Card" && (depositRequired || enableExtraDeposit)) {
            if (!cardDetails.card_number) {
                Swal.fire({ icon: "error", title: "Missing Information", text: "Card number is required" });
                return;
            }
            if (!cardDetails.card_expiry_year) {
                Swal.fire({ icon: "error", title: "Missing Information", text: "Card expiry date is required" });
                return;
            }
            if (!cardDetails.card_cvv) {
                Swal.fire({ icon: "error", title: "Missing Information", text: "Card CVV is required" });
                return;
            }

            // Extract MM and YY from "MM / YY" formatted string
            const expiryParts = cardDetails.card_expiry_year.replace(/\s/g, '').split('/');
            if (expiryParts.length === 2) {
                card_expiry_month = expiryParts[0];
                card_expiry_year = expiryParts[1];
            }
        }

        // 4. Construct API Payload
        let payloadData = {
            tour_id: tourId,

            trip_start_date: tourDetails?.duartion_start_date ? moment(tourDetails.duartion_start_date).format("YYYY-MM-DDThh:mm:ss") : null,
            trip_end_date: tourDetails?.duartion_end_date ? moment(tourDetails.duartion_end_date).format("YYYY-MM-DDThh:mm:ss") : null,
            mode_of_tour: data.mode_of_tour, // Assuming this is set somewhere in your form
            number_of_participants_with_airfare: "1",
            number_of_participants_with_out_airfare: "0",
            intrested_tour: mainTourDetails?.tour_names,
            nationality: data.country,
            enquiry_status: "Pending",
            place_of_birth: data.country,
            state: data.state,
            user: 1, // Static as per your original code

            // Personal Details
            first_name: formatName(data.first_name),
            middle_name: formatName(data.middle_name),
            last_name: formatName(data.last_name),
            gender: data.gender,
            email_address: data.email_address,
            phone_number: data.mobile_number,
            date_of_birth: formatToDate(data.date_of_birth),
            passport_number: data.passport_number,
            passport_expiration_date: formatApiDate(data.passport_expiration_date),

            // Primary Address
            city: formatName(data.city),
            address: formatName(getAddressValue(address1)),
            zip: data.zip,

            // Options
            accommodation_only: options.accommodation,
            single_room_cost_only: options.singleRoomCost,
            other_cost_included: options.otherCost,
            full_package: options.fullPackage,
            insurance: options.insurance,
            remarks: data.remarks,
            single_room_cost: tourDetails?.single_room_cost || 0,
            special_ticket_preference: watch("special_ticket_preference"),
            special_ticket_price: null,

            // Payment Details
            payment_method: data.payment_method,
            advance_payment: paymentDue, // Uses the calculation we built earlier

            // Card Specifics
            card_holder_name: (data.payment_method === "Card" && paymentDue > 0) ? formatName(data.card_holder_name) : null,
            card_expiry_month: card_expiry_month,
            card_expiry_year: card_expiry_year,

            // Billing Address
            billing_address: data.payment_method !== "Zelle" ? formatName(sameAsAbove ? getAddressValue(address1) : getAddressValue(address2)) : null,
            billing_city: data.payment_method !== "Zelle" ? (sameAsAbove ? formatName(data.city) : formatName(data.billing.city)) : null,
            billing_state: data.payment_method !== "Zelle" ? (sameAsAbove ? data.state : data.billing.state) : null,
            billing_zip: data.payment_method !== "Zelle" ? (sameAsAbove ? data.zip : data.billing.zip) : null,
            billing_country: data.payment_method !== "Zelle" ? (sameAsAbove ? data.country : data.billing.country) : null,
        };

        if (sub_Tour_url_name) {

            payloadData.sub_tour_id = subTourId

        }

        const submitData = {
            data: payloadData,
            card_number: data.payment_method === "Card" && paymentDue > 0 ? cardDetails.card_number : null,
            card_cvv: data.payment_method === "Card" && paymentDue > 0 ? cardDetails.card_cvv : null,
        };

        // 5. Submit Form
        setbackdropopen(true); // Optional: If you have a loading state for a spinner

        try {
            const formData = new FormData();
            formData.append("data", JSON.stringify(submitData));

            const response = await axios.post(`${base_url.api1}/tourapp/trip_enquiry`, formData);

            if (response.status === 200) {
                navigate("/thank-you-page", {
                    state: {
                        isClaimed: 3,
                        unique_no: data.mobile_number,
                        message: response?.data?.message || response?.data?.success_message
                    },
                });
                reset(); // Resets React Hook Form
                setAddress1("");
                setAddress2("");
            } else {
                navigate("/thank-you-page", {
                    state: {
                        isClaimed: 4,
                        unique_no: data.mobile_number,
                        message: response?.data?.error_message ?? response?.data?.message,
                    },
                });
                reset();
            }
        } catch (error) {
            if (error?.response?.status >= 400 && error?.response?.status < 500) {
                Swal.fire({
                    icon: "error",
                    title: error?.response?.data?.message || "Oops...",
                    text: "Something went wrong!",
                    showConfirmButton: false,
                    showCloseButton: true,
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: "Something went wrong!",
                    showConfirmButton: false,
                    showCloseButton: true,
                });
            }
        } finally {
            setbackdropopen(false);
        }
    };



    const hasCostOptions = options.fullPackage || options.accommodation || options.singleRoomCost || options.insurance;

    return (
        <div style={{ backgroundColor: "#F5F6FA" }}>
            <React.Fragment>
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={backdropopen}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Nav />

            </React.Fragment>


            <Container size="xl" py="xl" style={{ backgroundColor: "#F5F6FA" }}>
                <Paper shadow="md" p="lg" radius="md">
                    <Title order={2} mb="md">{sub_Tour_url_name ? tourDetails?.sub_tour_names || "Tour Enrollment" : tourDetails?.tour_names || "Tour Enrollment"}</Title>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Personal Details */}
                        <Title order={4} mt="xl">Personal Details</Title>
                        <Divider my="sm" />
                        <Grid>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput isFirstLetter={true} control={control} name="first_name" label="First Name" withAsterisk leftSection={<FiUser size={16} />} rules={{ required: "Required" }} /></Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput isFirstLetter={true} control={control} name="middle_name" label="Middle Name" /></Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput isFirstLetter={true} control={control} name="last_name" label="Last Name" withAsterisk leftSection={<FiUser size={16} />} rules={{ required: "Required" }} /></Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFSelect control={control} name="gender" label="Gender" onKeyDown={(e) => {
                                const key = e.key.toLowerCase();

                                if (key === 'm') {
                                    setValue('gender', 'Male');
                                } else if (key === 'f') {
                                    setValue('gender', 'Female');
                                } else if (key === 'o') {
                                    setValue('gender', 'Other');
                                }
                            }} withAsterisk data={["Male", "Female", "Other"]} rules={{ required: "Required" }} /></Grid.Col>

                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <Input.Wrapper label="Date of Birth">
                                    <Controller name="date_of_birth" control={control} render={({ field }) => (
                                        <RHFCustomDatePicker value={field.value ?? ''} onChange={(val) => field.onChange(val)} />
                                    )} />
                                </Input.Wrapper>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <RHFNumberInput
                                    name="mobile_number"
                                    control={control}
                                    label="Mobile Number"
                                    maxLength={10}
                                    rules={{
                                        required: "Mobile number is required",
                                        validate: (value) =>
                                            value?.length === 10 || "Mobile number must be 10 digits",
                                    }}
                                    withAsterisk leftSection={<FiPhone size={16} />}
                                />

                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput control={control} name="email_address" label="Email" withAsterisk leftSection={<FiMail size={16} />} rules={{ required: "Required" }} /></Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput control={control} name="passport_number" label="Passport Number" /></Grid.Col>

                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <Input.Wrapper label="Passport Expiration">
                                    <Controller name="passport_expiration_date" control={control} render={({ field }) => (
                                        <RHFCustomDatePicker value={field.value ?? ''} onChange={(val) => field.onChange(val)} maxYear={2099} />
                                    )} />
                                </Input.Wrapper>
                            </Grid.Col>
                        </Grid>

                        {/* Address */}
                        <Title order={4} mt="xl">Address</Title>
                        <Divider my="sm" />
                        <Grid>
                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <Input.Wrapper label="Address">
                                    <RHFGoogleAddress googleAddress={address1?.label} setGoogleAddress={setAddress1} isRequired={false} />
                                </Input.Wrapper>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput control={control} name="city" label="City" withAsterisk rules={{ required: "Required" }} /></Grid.Col>



                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <Input.Wrapper label="State" withAsterisk error={errors.state?.message}>
                                    <Controller name="state" control={control} rules={{ required: "Required" }} render={({ field }) => (
                                        <RegionDropdown country={selectedCountry} style={selectStyle} defaultOptionLabel="- Select State -" value={field.value} onChange={(val) => field.onChange(val)} />
                                    )}
                                    />
                                </Input.Wrapper>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput control={control} name="zip" label="Zip Code" withAsterisk rules={{ required: "Required" }} /></Grid.Col>

                            <Grid.Col span={{ base: 12, md: 3 }}>
                                <Input.Wrapper label="Country" withAsterisk error={errors.country?.message}>
                                    <Controller name="country" control={control} rules={{ required: "Required" }} render={({ field }) => (
                                        <CountryDropdown style={selectStyle} defaultOptionLabel="- Select Country -" value={field.value} onChange={(val) => { field.onChange(val); setValue('state', ''); }} />
                                    )}
                                    />
                                </Input.Wrapper>
                            </Grid.Col>
                        </Grid>



                        {/* Cost & Payment */}
                        <Title order={4} mt="xl">Cost and Options</Title>
                        <Divider my="sm" />



                        {/* <Grid> */}
                        {/* <Grid.Col span={{ base: 12, md: 6 }}> */}
                        <Group mt="xl" >
                            <RHFCheckbox mr='xl' control={control} name="options.fullPackage" label={`Full Package ($${tourDetails?.full_package_price || 0})`} description='' />
                            <RHFCheckbox mr='xl' control={control} name="options.accommodation" label={`Accommodation Only ($${tourDetails?.accommodation_only_price || 0})`} description='' />
                            <RHFCheckbox mr='xl' control={control} name="options.singleRoomCost" label={`Private Room Cost ($${tourDetails?.single_room_cost || 0})`} description='' />
                            <RHFCheckbox mr='xl' control={control} name="options.insurance" label={`Travel Insurance ($${tourDetails?.insurance_price || 0})`} description='' />
                            <RHFCheckbox mr='xl' control={control} name="options.otherCost" label={`Other Cost ($${tourDetails?.other_cost || 0})`} description={tourDetails?.other_cost_description ? `(${tourDetails?.other_cost_description})` : ''} />
                        </Group>

                        <Input.Wrapper label="Special Ticket Arrangement (Not the group tickets)" size="md" mt={'xl'} mb="xl">
                            <Group mt="lg" spacing="xl" gap={'lg'}>
                                {ticketOptions.map((item) => (
                                    <Checkbox
                                        key={item.value}
                                        label={item.label}
                                        styles={{
                                            label: {
                                                fontSize: '15px'
                                            }
                                        }}
                                        checked={watch("special_ticket_preference") === item.value}
                                        onChange={() => {
                                            const current = watch("special_ticket_preference");
                                            setValue("special_ticket_preference", current === item.value ? "" : item.value);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    />
                                ))}
                            </Group>
                        </Input.Wrapper>

                        {/* {tourDetails?.other_cost_description && (
                            <div className="description-highlight">
                                {tourDetails.other_cost_description}
                            </div>
                        )} */}
                        {/* </Grid.Col> */}

                        {/* <Grid.Col span={{ base: 12, md: 6 }}> */}
                        <Paper p="md" withBorder bg="gray.0">
                            <Title order={5}>Total Cost Summary</Title>
                            <Text size="xl" style={{ fontWeight: 600 }} ml={'md'} weight={600} color="blue">${totalPrice.toFixed(2)}</Text>
                            <Divider my="sm" />
                            <Grid align="flex-end">
                                <Grid.Col span={{ base: 12, md: 3 }}>
                                    <RHFSelect control={control} name="payment_method" label="Payment Method" data={tourDetails?.payment_methods?.length > 0 ? tourDetails?.payment_methods : ["Cash", "Card", "Check", "Zelle"]} />

                                </Grid.Col>

                                {paymentMethod === 'Card' && hasCostOptions && (
                                    <>

                                        <Grid.Col span={{ base: 12, md: 3 }}>
                                            <RHFCheckbox
                                                control={control}
                                                name="depositRequired"
                                                label={
                                                    <div className="deposit-text-stack">
                                                        <span className="deposit-title">
                                                            Deposit {minimumDepositRequired ? '(Mandatory)' : ''}
                                                        </span>
                                                        <span className="deposit-subtitle">
                                                            (Minimum ${minimumDeposit})
                                                        </span>
                                                    </div>
                                                }
                                            />


                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 3 }}>
                                            <Group align="flex-end" spacing="xs">
                                                <RHFCheckbox control={control} name="enableExtraDeposit" mb={10} />
                                                <RHFNumberInput
                                                    control={control}
                                                    name="extraDepositAmount"
                                                    label="Extra Payment"
                                                    allowNegative={false}
                                                    allowDecimal
                                                    disabled={!enableExtraDeposit}
                                                    leftSection={<FiDollarSign size={14} />}
                                                    placeholder="Amount"
                                                    rules={{
                                                        validate: (value) => !enableExtraDeposit || value > 0 || "Required"
                                                    }}
                                                />
                                            </Group>
                                        </Grid.Col>



                                    </>

                                )}

                                {(paymentMethod === 'Card' || paymentMethod === 'Zelle') && hasCostOptions && <Grid.Col span={{ base: 12, md: 3 }}>


                                    <RHFTextInput
                                        control={control}
                                        name="advance_payment"
                                        readOnly={paymentMethod === 'Card' ? true : false}
                                        label="Payment"
                                        // disabled={true}
                                        leftSection={<FiDollarSign size={14} />}
                                        placeholder="Amount"

                                    />

                                </Grid.Col>}


                            </Grid>


                        </Paper>
                        {/* </Grid.Col> */}
                        {/* </Grid> */}



                        {/* Credit Card Inputs */}
                        {paymentMethod === "Card" && (
                            <>
                                <Title order={4} mt="xl">Credit Card Details</Title>
                                <Divider my="sm" />
                                <Grid>
                                    <Grid.Col span={{ base: 12, md: 4 }}>
                                        <RHFTextInput control={control} name="card_holder_name" label="Name on Card" withAsterisk rules={{ required: "Required" }} />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, md: 4 }}>
                                        <Input.Wrapper label="Card Number" withAsterisk>
                                            <TextInput
                                                rightSection={<svg {...getCardImageProps({ images })} style={{ width: "24px", height: "auto" }} />}
                                                rightSectionWidth={40}
                                                styles={{

                                                    input: {
                                                        height: '40px'
                                                    }
                                                }}
                                                {...getCardNumberProps({ onChange: (e) => setCardDetails(prev => ({ ...prev, card_number: e.target.value })) })}
                                            />
                                        </Input.Wrapper>
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 6, md: 2 }}>
                                        <Input.Wrapper label="Expiry Date" withAsterisk>
                                            <TextInput styles={{

                                                input: {
                                                    height: '40px'
                                                }
                                            }}{...getExpiryDateProps({ onChange: (e) => setCardDetails(prev => ({ ...prev, card_expiry_year: e.target.value })) })} />
                                        </Input.Wrapper>
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 6, md: 2 }}>
                                        <Input.Wrapper label="CVV" withAsterisk>
                                            <TextInput styles={{

                                                input: {
                                                    height: '40px'
                                                }
                                            }} {...getCVCProps({ onChange: (e) => setCardDetails(prev => ({ ...prev, card_cvv: e.target.value })) })} />
                                        </Input.Wrapper>
                                    </Grid.Col>
                                </Grid>

                                <Title order={4} mt="xl">Billing Address</Title>
                                <Divider my="sm" />
                                <RHFCheckbox control={control} name="sameAsAbove" label="Same as Personal Address" mt="xs" mb="md" />
                                {!sameAsAbove && (
                                    <Grid>
                                        <Grid.Col span={{ base: 12, md: 3 }}>
                                            <Input.Wrapper label="Billing Address">
                                                <RHFGoogleAddress googleAddress={address2?.label} setGoogleAddress={setAddress2} isRequired={false} />
                                            </Input.Wrapper>
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput control={control} name="billing.city" label="City" /></Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 3 }}><RHFTextInput control={control} name="billing.zip" label="Zip" /></Grid.Col>

                                        <Grid.Col span={{ base: 12, md: 3 }}>
                                            <Input.Wrapper label="Country">
                                                <Controller name="billing.country" control={control} render={({ field }) => (
                                                    <CountryDropdown style={selectStyle} defaultOptionLabel="- Select Country -" value={field.value} onChange={(val) => { field.onChange(val); setValue('billing.state', ''); }} />
                                                )}
                                                />
                                            </Input.Wrapper>
                                        </Grid.Col>

                                        <Grid.Col span={{ base: 12, md: 3 }}>
                                            <Input.Wrapper label="State">
                                                <Controller name="billing.state" control={control} render={({ field }) => (
                                                    <RegionDropdown country={selectedBillingCountry} style={selectStyle} defaultOptionLabel="- Select State -" value={field.value} onChange={(val) => field.onChange(val)} />
                                                )}
                                                />
                                            </Input.Wrapper>
                                        </Grid.Col>
                                    </Grid>
                                )}
                            </>
                        )}

                        <Title order={4} mt="xl">Additional Information</Title>
                        <Divider my="sm" />
                        <Grid>
                            <Grid.Col span={{ base: 12, md: 6 }}><RHFTextarea control={control} name="remarks" label="Remarks / Special Requests" minRows={4} /></Grid.Col>
                        </Grid>

                        {/* Submit Actions */}
                        <Group position="right" mt="xl">
                            <Button variant="outline" onClick={() => reset()} disabled={loading}>Reset</Button>
                            <Button type="submit" loading={loading} color="blue">Submit Enrollment</Button>
                        </Group>

                    </form>
                </Paper>
            </Container>

            <FooterNew />

        </div>
    );
};

export default TourTripEnrollment;